import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import toast from "react-hot-toast";
import apiClient from "../../../services/apiClient";
import PageLoader from "../../Loading/PageLoader";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

const VoiceConfigsList = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Modal + form state
  const [openModal, setOpenModal] = useState(false);
  const [editingVoice, setEditingVoice] = useState(null);
  const [formData, setFormData] = useState({
    voice_name: "",
    voice_code: "",
    language_id: "",
    provider: "",
    accent: "",
    style: "",
    gender: "",
    status: true,
    voice_sample: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchVoices();
  }, [pagination.pageIndex, pagination.pageSize]);

  // Fetch all voices
  const fetchVoices = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/voice-configs", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });
      setVoices(response.data || []);
      setPaginationInfo({
        total_count: response.pagination?.total_count || 0,
        total_pages: response.pagination?.total_pages || 0,
      });
    } catch (err) {
      toast.error("Failed to fetch voice configs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));
  };

  const handleCreate = async () => {
    if (!formData.voice_name) return toast.error("Voice name is required");
    setLoading(true);
    try {
      await apiClient.post("/voice-configs", formData);
      toast.success("Voice config created successfully");
      handleCloseModal();
      fetchVoices();
    } catch (err) {
      toast.error("Failed to create voice config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (voice) => {
    setEditingVoice(voice);
    setFormData({
      voice_name: voice.voice_name,
      voice_code: voice.voice_code,
      language_id: voice.language_id,
      provider: voice.provider,
      accent: voice.accent,
      style: voice.style,
      gender: voice.gender,
      status: voice.status,
      voice_sample: voice.voice_sample,
    });
    setOpenModal(true);
  };

  const handleUpdate = async () => {
    if (!editingVoice) return;
    setLoading(true);
    try {
      await apiClient.put(`/voice-configs/${editingVoice._id}`, formData);
      toast.success("Voice config updated successfully");
      handleCloseModal();
      fetchVoices();
    } catch (err) {
      toast.error("Failed to update voice config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this voice config?"))
      return;
    setLoading(true);
    try {
      await apiClient.delete(`/voice-configs/${id}`);
      toast.success("Voice config deleted successfully");
      fetchVoices();
    } catch (err) {
      toast.error("Failed to delete voice config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingVoice(null);
    setFormData({
      voice_name: "",
      voice_code: "",
      language_id: "",
      provider: "",
      accent: "",
      style: "",
      gender: "",
      status: true,
      voice_sample: "",
    });
  };

  const columns = [
    { accessorKey: "voice_name", header: "Voice Name" },
    { accessorKey: "voice_code", header: "Code" },
    { accessorKey: "language_id", header: "Language ID" },
    { accessorKey: "provider", header: "Provider" },
    { accessorKey: "accent", header: "Accent" },
    { accessorKey: "style", header: "Style" },
    { accessorKey: "gender", header: "Gender" },
    {
      accessorKey: "status",
      header: "Status",
      Cell: ({ row }) => (
        <Switch checked={row.original.status} disabled size="small" />
      ),
    },
    {
      accessorKey: "voice_sample",
      header: "Sample",
      Cell: ({ cell }) => {
        const value = cell.getValue();
        const audioSrc =
          value && !value.startsWith("http") ? `${BASE_URL}${value}` : value;

        return value ? (
          <audio
            controls
            src={audioSrc}
            style={{ width: "140px" }}
          >
            Your browser does not support the audio element.
          </audio>
        ) : (
          "N/A"
        );
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      size: 150,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.original._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && voices.length === 0) return <PageLoader />;

  return (

    <>

    {/* Header */}
    <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
      <h3>Voice Configs</h3>
      <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          New Config
        </Button>
    </div>
    
    <Box sx={{ p: 3 }}>
      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search voices..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
        />
      </Box>

      {/* Table */}
      <MaterialReactTable
        columns={columns}
        data={voices.filter((v) =>
          v.voice_name?.toLowerCase().includes(searchText.toLowerCase())
        )}
        manualPagination
        rowCount={paginationInfo.total_count}
        onPaginationChange={setPagination}
        state={{
          isLoading: loading,
          pagination: pagination,
        }}
        enableRowActions={false}
        enableColumnActions={false}
        enableSorting
        enablePagination
        muiTablePaginationProps={{
          showFirstButton: true,
          showLastButton: true,
        }}
      />

      {/* Popup Form */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingVoice ? "Edit Voice Config" : "Create New Voice Config"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
            {[
              "voice_name",
              "voice_code",
              "language_id",
              "provider",
              "accent",
              "style",
              "gender",
              "voice_sample",
            ].map((field) => (
              <TextField
                key={field}
                label={field.replace("_", " ").toUpperCase()}
                name={field}
                value={formData[field]}
                onChange={handleChange}
                fullWidth
              />
            ))}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography>Status</Typography>
              <Switch
                checked={formData.status}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, status: e.target.checked }))
                }
              />
              <Chip
                label={formData.status ? "Active" : "Inactive"}
                color={formData.status ? "success" : "default"}
                size="small"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingVoice ? handleUpdate : handleCreate}
          >
            {editingVoice ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    
    </>

  );
};

export default VoiceConfigsList;
